"use client";

import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useBooking } from "../context";
import {
  availableFilters,
  classTypesOf,
  dayKeyToDate,
  daySummary,
  groupByDay,
  matchesFilter,
  type ScheduleFilter,
  type ScheduleNotice,
  type ScheduleSession,
  type ScheduleWeek,
} from "../schedule";
import { ClassCard, ClassDot, type ClassAction } from "./class-card";
import { WeekGrid } from "./week-grid";

const focusRing =
  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-studio-ink";

/**
 * Week navigation, day strip, filters and the selected day's classes.
 * Week changes are links (the host fetches the new week); day and filter
 * changes stay on the client.
 *
 * `wideLayout="week"` (the widget) shows the whole week as a grid from the
 * `md` width up; in an iframe that width is the host page's column.
 */
export function ScheduleView({
  sessions,
  week,
  previousHref,
  nextHref,
  todayHref,
  initialDay,
  getAction,
  onDayChange,
  account,
  wideLayout = "list",
  showClassTypes = false,
  notices = [],
}: {
  sessions: ScheduleSession[];
  week: ScheduleWeek;
  previousHref: string;
  nextHref: string;
  /** Shown as a "Today" button when another week is displayed. */
  todayHref?: string;
  initialDay: string;
  getAction: (session: ScheduleSession) => ClassAction;
  onDayChange?: (day: string) => void;
  /** Log in / my space, at the end of the header. */
  account?: React.ReactNode;
  wideLayout?: "list" | "week";
  /** Colour dots and one filter per class type (the widget). Off on the hosted site. */
  showClassTypes?: boolean;
  /** Studio closures to announce above the week. */
  notices?: ScheduleNotice[];
}) {
  const { t, fmt, timeZone } = useBooking();
  const [selectedDay, setSelectedDay] = useState(initialDay);
  const [filter, setFilter] = useState<ScheduleFilter>("all");

  const filters = useMemo(
    () => availableFilters(sessions, { classTypes: showClassTypes }),
    [sessions, showClassTypes],
  );
  const typesById = useMemo(
    () => new Map(classTypesOf(sessions).map((type) => [type.id, type])),
    [sessions],
  );
  const filtered = useMemo(
    () => sessions.filter((s) => matchesFilter(s, filter)),
    [sessions, filter],
  );
  const byDay = useMemo(() => groupByDay(filtered, timeZone), [filtered, timeZone]);
  const daySessions = byDay.get(selectedDay) ?? [];
  const headingId = `day-${selectedDay}`;

  const selectDay = (day: string) => {
    setSelectedDay(day);
    onDayChange?.(day);
  };

  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-4">
      {notices.map((notice) => (
        <ClosureNotice key={notice.id} notice={notice} />
      ))}
      <WeekNav
        week={week}
        label={fmt.dateRange(
          dayKeyToDate(week.days[0]!, timeZone),
          dayKeyToDate(week.days[6]!, timeZone),
        )}
        previousHref={previousHref}
        nextHref={nextHref}
        todayHref={todayHref}
        account={account}
        filters={
          filters.length > 0 && (
            <div role="group" aria-label={t("schedule.filters")} className="flex flex-wrap gap-2">
              {filters.map((f) => {
                const type = f.startsWith("type:") ? typesById.get(f.slice(5)) : undefined;
                return (
                  <button
                    key={f}
                    type="button"
                    aria-pressed={filter === f}
                    onClick={() => setFilter(f)}
                    className={[
                      "inline-flex h-11 items-center gap-2 rounded-pill px-4 text-sm font-semibold transition-colors duration-150 md:h-9",
                      focusRing,
                      filter === f
                        ? "bg-ink text-surface"
                        : "border border-line bg-surface text-ink-soft hover:text-ink",
                    ].join(" ")}
                  >
                    {type ? (
                      <>
                        <ClassDot color={type.color} />
                        {type.name}
                      </>
                    ) : (
                      t(`schedule.${f === "studio" ? "inStudio" : (f as "all" | "online" | "workshops")}`)
                    )}
                  </button>
                );
              })}
            </div>
          )
        }
      />

      {wideLayout === "week" && (
        <div className="hidden md:block">
          <WeekGrid
            sessions={filtered}
            week={week}
            getAction={getAction}
            showColor={showClassTypes}
          />
        </div>
      )}

      <div className={wideLayout === "week" ? "flex flex-col gap-4 md:hidden" : "contents"}>
      <div
        role="group"
        aria-label={t("schedule.days")}
        className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 md:mx-0 md:grid md:grid-cols-7 md:overflow-visible md:px-0"
      >
        {week.days.map((day) => {
          const date = dayKeyToDate(day, timeZone);
          const { open: count, closed } = daySummary(byDay.get(day));
          const label = closed ? t("schedule.dayClosed") : t("schedule.classCount", { count });
          const selected = day === selectedDay;
          return (
            <button
              key={day}
              type="button"
              aria-pressed={selected}
              aria-label={`${fmt.dayLong(date)}, ${label}`}
              onClick={() => selectDay(day)}
              className={[
                "flex min-w-14 shrink-0 flex-col items-center gap-0.5 rounded-card border px-2 py-2.5 transition-colors duration-150 md:min-w-0",
                focusRing,
                selected
                  ? "border-studio-accent bg-studio-accent text-studio-on-accent"
                  : "border-line bg-surface text-ink hover:border-line-strong",
              ].join(" ")}
            >
              <span className={`text-xs font-medium ${selected ? "" : "text-ashe"}`}>
                {fmt.weekdayShort(date)}
              </span>
              <span className="font-display text-xl font-semibold leading-none">
                {fmt.dayNumber(date)}
              </span>
              <span
                aria-hidden="true"
                className={`hidden text-xs md:block ${selected ? "" : count ? "text-ink-soft" : "text-ink-faint"}`}
              >
                {label}
              </span>
              <span
                aria-hidden="true"
                className={`size-1.5 rounded-pill md:hidden ${count ? (selected ? "bg-studio-on-accent" : "bg-studio-accent") : "bg-transparent"}`}
              />
            </button>
          );
        })}
      </div>

      <h2 id={headingId} className="mt-2 font-display text-xl font-medium text-ink first-letter:uppercase">
        {fmt.dayLong(dayKeyToDate(selectedDay, timeZone))}
      </h2>

      {daySessions.length > 0 ? (
        <ul className="flex flex-col gap-3">
          {daySessions.map((session) => (
            <li key={session.id}>
              <ClassCard session={session} action={getAction(session)} showColor={showClassTypes} />
            </li>
          ))}
        </ul>
      ) : (
        <EmptyDay weekIsEmpty={byDay.size === 0} nextHref={nextHref} />
      )}
      </div>
    </section>
  );
}

function WeekNav({
  week,
  label,
  previousHref,
  nextHref,
  todayHref,
  account,
  filters,
}: {
  week: ScheduleWeek;
  label: string;
  previousHref: string;
  nextHref: string;
  todayHref?: string;
  account?: React.ReactNode;
  filters: React.ReactNode;
}) {
  const { t, Link } = useBooking();
  const arrow = `flex size-11 items-center justify-center rounded-pill border border-line bg-surface text-ink transition-colors duration-150 hover:border-line-strong ${focusRing}`;

  return (
    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
      <div className="flex items-center gap-2">
        {week.isCurrent ? (
          <span aria-hidden="true" className={`${arrow} pointer-events-none opacity-40`}>
            <ChevronLeft size={18} />
          </span>
        ) : (
          <Link href={previousHref} scroll={false} aria-label={t("schedule.previousWeek")} className={arrow}>
            <ChevronLeft size={18} aria-hidden="true" />
          </Link>
        )}
        <p className="min-w-40 text-center text-base font-semibold text-ink" aria-live="polite">
          {label}
        </p>
        <Link href={nextHref} scroll={false} aria-label={t("schedule.nextWeek")} className={arrow}>
          <ChevronRight size={18} aria-hidden="true" />
        </Link>
        {todayHref && !week.isCurrent && (
          <Link
            href={todayHref}
            scroll={false}
            className={`inline-flex h-11 items-center rounded-pill border border-line bg-surface px-4 text-sm font-semibold text-ink transition-colors duration-150 hover:border-line-strong md:h-9 ${focusRing}`}
          >
            {t("schedule.today")}
          </Link>
        )}
        {account && <div className="ml-auto md:hidden">{account}</div>}
      </div>
      {(filters || account) && (
        <div className="flex flex-wrap items-center gap-3 md:justify-end">
          {filters}
          {account && <div className="hidden md:block">{account}</div>}
        </div>
      )}
    </div>
  );
}

function EmptyDay({ weekIsEmpty, nextHref }: { weekIsEmpty: boolean; nextHref: string }) {
  const { t, Link } = useBooking();
  return (
    <div className="flex flex-col items-center gap-3 rounded-card border border-line bg-surface px-4 py-10 text-center">
      <p className="text-sm text-ink-soft">
        {weekIsEmpty ? t("schedule.noClassesWeek") : t("schedule.noClassesDay")}
      </p>
      {weekIsEmpty && (
        <Link
          href={nextHref}
          scroll={false}
          className={`inline-flex h-11 items-center rounded-pill border border-line px-5 text-sm font-semibold text-ink hover:border-line-strong ${focusRing}`}
        >
          {t("schedule.seeNextWeek")}
        </Link>
      )}
    </div>
  );
}

/** "Closed 21 – 27 December · Winter break", and the studio's message. */
function ClosureNotice({ notice }: { notice: ScheduleNotice }) {
  const { t, fmt, timeZone } = useBooking();
  const from = dayKeyToDate(notice.startDate, timeZone);
  const to = dayKeyToDate(notice.endDate, timeZone);
  const dates = notice.startDate === notice.endDate ? fmt.dayMonth(from) : fmt.dateRange(from, to);

  return (
    <div role="status" className="rounded-card border border-line bg-info-soft px-4 py-3 text-sm text-ink">
      <p className="font-semibold">{t("schedule.closedNotice", { dates, name: notice.name })}</p>
      {notice.message ? <p className="mt-1 whitespace-pre-line text-ink-soft">{notice.message}</p> : null}
    </div>
  );
}
